/*
 * PER-1218 phase 2 — a small, dependency-free static BVH over chunk
 * manifest AABBs.
 *
 * Deliberately NOT `three-mesh-bvh`: that library indexes individual
 * mesh triangles for precise raycasting against a single dense mesh.
 * A chunk manifest is a handful (tens, maybe low hundreds) of
 * top-level AABBs — using a triangle-BVH library for that would be
 * the wrong tool represented as the right one. This module is pure
 * TS (no `three` import) so it can be unit-tested without a DOM or a
 * WebGL context; `chunk-manager.ts` (which does import `three`)
 * builds a `THREE.Frustum`-backed predicate and passes it in here as
 * a plain closure.
 */

export interface ChunkBox {
  id: number;
  min: [number, number, number];
  max: [number, number, number];
}

type BvhNode =
  | { kind: 'leaf'; box: ChunkBox }
  | {
      kind: 'internal';
      min: [number, number, number];
      max: [number, number, number];
      left: BvhNode;
      right: BvhNode;
    };

/** Caller-supplied box/frustum intersection test. */
export type IntersectsBox = (min: [number, number, number], max: [number, number, number]) => boolean;

function unionMin(a: [number, number, number], b: [number, number, number]): [number, number, number] {
  return [Math.min(a[0], b[0]), Math.min(a[1], b[1]), Math.min(a[2], b[2])];
}
function unionMax(a: [number, number, number], b: [number, number, number]): [number, number, number] {
  return [Math.max(a[0], b[0]), Math.max(a[1], b[1]), Math.max(a[2], b[2])];
}

function boundsOf(boxes: ChunkBox[]): { min: [number, number, number]; max: [number, number, number] } {
  let min = boxes[0]!.min;
  let max = boxes[0]!.max;
  for (const b of boxes.slice(1)) {
    min = unionMin(min, b.min);
    max = unionMax(max, b.max);
  }
  return { min, max };
}

function build(boxes: ChunkBox[]): BvhNode {
  if (boxes.length === 1) return { kind: 'leaf', box: boxes[0]! };

  const { min, max } = boundsOf(boxes);
  // Split on the largest axis of the union bounds, at the median
  // center-point of the boxes along that axis.
  const extents: [number, number, number] = [max[0] - min[0], max[1] - min[1], max[2] - min[2]];
  let axis: 0 | 1 | 2 = 0;
  if (extents[1] > extents[axis]) axis = 1;
  if (extents[2] > extents[axis]) axis = 2;

  const sorted = [...boxes].sort((a, b) => {
    const ca = (a.min[axis] + a.max[axis]) / 2;
    const cb = (b.min[axis] + b.max[axis]) / 2;
    return ca - cb;
  });
  const mid = Math.ceil(sorted.length / 2);
  const leftBoxes = sorted.slice(0, mid);
  const rightBoxes = sorted.slice(mid);

  const left = build(leftBoxes);
  const right = build(rightBoxes);
  return { kind: 'internal', min, max, left, right };
}

/** Static median-split binary BVH over chunk manifest AABBs. */
export class ChunkBvh {
  private readonly root: BvhNode | null;

  constructor(boxes: ChunkBox[]) {
    this.root = boxes.length === 0 ? null : build(boxes);
  }

  /**
   * Return the ids of every leaf box for which `intersectsBox`
   * returns true, pruning subtrees whose union bounding box already
   * fails the predicate.
   */
  query(intersectsBox: IntersectsBox): number[] {
    if (!this.root) return [];
    const out: number[] = [];
    const visit = (node: BvhNode): void => {
      if (node.kind === 'leaf') {
        if (intersectsBox(node.box.min, node.box.max)) out.push(node.box.id);
        return;
      }
      if (!intersectsBox(node.min, node.max)) return;
      visit(node.left);
      visit(node.right);
    };
    visit(this.root);
    return out;
  }
}
